import { HTMLAttributes, forwardRef, useMemo, useRef, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button, F0Icon } from '@factorialco/f0-react'
import { F0AvatarPerson } from '@factorialco/f0-react'
import { Desktop, Maximize, MicrophoneNegative, Minimize } from '@factorialco/f0-react/icons/app'
import type { TrackReferenceOrPlaceholder } from '@livekit/components-core'
import { isTrackReference } from '@livekit/components-core'
import {
  useAudioWaveform,
  useConnectionQualityIndicator,
  useIsMuted,
  useIsSpeaking,
} from '@livekit/components-react'
import { Track } from 'livekit-client'
import type { Participant } from 'livekit-client'

import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { SpeakingIndicator } from 'modules/meetings/components/VideoGrid/components/SpeakingIndicator'
import { VideoTrack } from 'modules/meetings/components/VideoGrid/components/VideoTrack'
import { useMeeting } from 'modules/meetings/lib/hooks/useMeeting'

interface ParticipantTileProps extends HTMLAttributes<HTMLElement> {
  trackRef: TrackReferenceOrPlaceholder
  participant: Participant
  numberOfTracks: number
  onFocus?: () => void
  onUnfocus?: () => void
  isFocused?: boolean
}

export const ParticipantTile = forwardRef<HTMLElement, ParticipantTileProps>(
  function MyParticipantTile(
    { trackRef, participant, numberOfTracks, onFocus, onUnfocus, isFocused },
    ref
  ) {
    const { meeting } = useMeeting()
    const attendees = meeting?.attendees || []

    const [isHovered, setIsHovered] = useState(false)
    const videoRef = useRef<HTMLVideoElement>(null)
    const isSpeaking = useIsSpeaking(participant)

    const micTrack = participant?.getTrackPublication(Track.Source.Microphone)
    const micRef: TrackReferenceOrPlaceholder = useMemo(() => {
      return {
        participant: participant,
        source: Track.Source.Microphone,
        publication: micTrack,
      }
    }, [participant, micTrack])

    const isMuted = useIsMuted(micRef)
    const audioWaveform = useAudioWaveform(micRef, {
      barCount: 5,
      volMultiplier: 10,
      updateInterval: 200,
    })

    const isVideoTrack =
      isTrackReference(trackRef) &&
      (trackRef.publication?.kind === 'video' ||
        trackRef.source === Track.Source.Camera ||
        trackRef.source === Track.Source.ScreenShare)

    const participantId = participant?.identity || 'Local'
    const isScreenShare = trackRef?.source === Track.Source.ScreenShare

    const attendee = attendees.find(
      ({ employee }) => Number(employee?.id) === Number(participantId)
    )

    const participantFirstName =
      attendee?.employee?.firstName || i18n.t('meetings.unknown_participant')
    const participantLastName = attendee?.employee?.lastName || ''
    const participantName = attendee?.employee?.fullName || i18n.t('meetings.unknown_participant')
    const participantAvatar = buildUrl(attendee?.employee?.avatar?.url) || undefined
    const { quality } = useConnectionQualityIndicator({ participant })

    const isLocalCamera =
      isTrackReference(trackRef) &&
      trackRef.participant.isLocal &&
      trackRef.source === Track.Source.Camera

    const facingMode = isLocalCamera ? 'user' : undefined

    const isTrackVideoEnabled =
      (isVideoTrack && trackRef?.participant?.isCameraEnabled && isTrackReference(trackRef)) ||
      isScreenShare

    const handleFullscreen = async () => {
      if (videoRef.current) {
        try {
          if (!document.fullscreenElement) {
            await videoRef.current.requestFullscreen()
          } else {
            await document.exitFullscreen()
          }
        } catch (error) {}
      }
    }

    return (
      <Box
        style={{
          boxShadow: isSpeaking && !isScreenShare ? '0px 0px 18px 0px #07A2AD99' : undefined,
        }}
        width="full"
        height="full"
        overflowY="hidden"
        overflowX="hidden"
        ref={ref}
        background="grey1000"
        position="relative"
        alignItems="center"
        justifyContent="center"
        border={{
          all: {
            width: 's1',
            style: isTrackVideoEnabled ? 'hidden' : 'solid',
            color: 'grey800',
          },
        }}
        borderRadius={{ all: 'abs016' }}
      >
        {isTrackVideoEnabled ? (
          <VideoTrack
            ref={videoRef}
            trackRef={trackRef}
            facingMode={facingMode}
            isScreenShare={isScreenShare}
          />
        ) : (
          <F0AvatarPerson
            firstName={participantFirstName}
            lastName={participantLastName}
            size="2xl"
            src={participantAvatar}
          />
        )}

        <Box
          position="absolute"
          style={{
            top: '8px',
            left: '8px',
            zIndex: '50',
            maxWidth: 'calc(100% - 44px)',
          }}
          background="grey1000"
          borderRadius={{ all: 'abs008' }}
          paddingY="s4"
          paddingX="s8"
          flexDirection="row"
        >
          <Box overflowX="hidden">
            <Text
              color="grey000White"
              size="100"
              weight="semibold"
              title={participantName}
              lineClamp={1}
            >
              {participantFirstName}
            </Text>
          </Box>
          <Box
            style={{
              transition: 'all 0.2s ease-in-out',
              width: isSpeaking || (isMuted && !isScreenShare) || isScreenShare ? 'auto' : '0',
              paddingLeft: isSpeaking || (isMuted && !isScreenShare) || isScreenShare ? '8px' : '0',
            }}
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
          >
            {isMuted && !isScreenShare && (
              <F0Icon icon={MicrophoneNegative} size="sm" color="critical-bold" />
            )}
            {isScreenShare && <F0Icon icon={Desktop} size="sm" color="info" />}
            {isSpeaking && !isScreenShare && !isMuted && (
              <SpeakingIndicator bars={audioWaveform.bars} />
            )}
          </Box>
        </Box>

        <Box
          style={{
            opacity: isHovered ? '1' : '0',
            inset: '0',
            transition: 'opacity 0.2s ease-in-out',
          }}
          position="absolute"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {numberOfTracks > 1 && trackRef && (
            <Box
              position="absolute"
              borderRadius={{ all: 'abs008' }}
              style={{
                top: '8px',
                right: '8px',
              }}
              flexDirection="row"
              gap="s8"
            >
              <Box
                borderRadius={{ all: 'abs008' }}
                background="grey000White"
                flexDirection="row"
                gap="s8"
              >
                <F0Button
                  size="sm"
                  hideLabel
                  variant="secondary"
                  onClick={isFocused ? onUnfocus : onFocus}
                  icon={isFocused ? Minimize : Maximize}
                />
              </Box>
              {isFocused && isScreenShare && (
                <Box
                  borderRadius={{ all: 'abs008' }}
                  background="grey000White"
                  flexDirection="row"
                  gap="s8"
                >
                  <F0Button
                    size="sm"
                    label={i18n.t('meetings.room.fullscreen')}
                    variant="secondary"
                    onClick={handleFullscreen}
                  />
                </Box>
              )}
            </Box>
          )}

          {quality && (
            <Box
              gap="s2"
              position="absolute"
              style={{
                bottom: '8px',
                right: '8px',
              }}
              flexDirection="row"
              alignItems="flexEnd"
              justifyContent="center"
              padding="s2"
            >
              <Box
                borderRadius={{ all: 'abs004' }}
                background="grey000White"
                width="s4"
                height="s4"
                style={{
                  opacity:
                    quality === 'excellent' || quality === 'good' || quality === 'poor'
                      ? '1'
                      : '0.3',
                }}
              />
              <Box
                borderRadius={{ all: 'abs004' }}
                background="grey000White"
                width="s4"
                height="s8"
                style={{
                  opacity: quality === 'excellent' || quality === 'good' ? '1' : '0.3',
                }}
              />
              <Box
                borderRadius={{ all: 'abs004' }}
                background="grey000White"
                width="s4"
                height="s12"
                style={{
                  opacity: quality === 'excellent' ? '1' : '0.3',
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    )
  }
)
